import React, {useMemo, useState} from "react";
import {
  SecurSpaceContainer,
  SecurSpaceReportContainer,
  SecurSpaceReportTitle
} from "../../components/common";
import { PersonAndBoxMessage } from "../../components/messages/PersonAndBoxMessage";
import {useSnackbar} from "../../context/snackbar-context";
import { useSnackbarDisplayOnError } from "../../hooks/error/useSnackbarDisplayOnError";
import { useBindSearchQuery } from "../../hooks/useBindSearchQuery";
import { useGetBoundReportPaginated } from "../../hooks/request/useGetBoundReportPaginated";
import { BoundTablePagination } from "../../components/common/inputs/boundInputs";
import { BookingCheckInVO } from "../../types/model/valueObject/booking/BookingCheckInVO";
import { SupplierBookingCheckInSortFilter, SupplierBookingCheckInSearchParams } from "../../components/supplier/bookingCheckInReport/SupplierBookingCheckInSortFilter";
import { SupplierBookingCheckInCardListWithLoader } from "../../components/supplier/bookingCheckInReport/SupplierBookingCheckInCardList";
import { SupplierBookingCheckInDialogWrapper } from "../../components/supplier/bookingCheckInReport/SupplierBookingCheckInDialogWrapper";
import { supplierBookingCheckInReportEndpoints as reportEndpoints} from "../../requests/api-endpoints";
import ReportDataSummary from "../../components/report/redesigned/ReportDataSummary";
import MobileAppPromo from "../../components/mobileAppPromo/MobileAppPromo";
import { useFeatureFlags } from "../../hooks/routing/useFeatureFlags";
import { ReportDialogSetterProps, getDefaultReportDialog, ReportDialogActionCloseProps } from "../../interfaces";

// a default query MUST be defined outside any components to avoid re-initialization on each render
const DEFAULT_QUERY: SupplierBookingCheckInSearchParams = {
  locationId: '',
  buyerAccountId: '',
  bookingNumber: '',
  sortBy: "START_DATE",
  sortDir: 'desc',
  page: 0,
  size: 25,
};

const SupplierBookingCheckInReport= () => {
  const { snackbarShowMessage } = useSnackbar();
  // state hooks
  const { mobileAppBanner } = useFeatureFlags();
  const [reportDialog, setReportDialog] = useState<ReportDialogSetterProps<BookingCheckInVO>>(getDefaultReportDialog());
  const [reportQuery, setReportQuery] = useBindSearchQuery(DEFAULT_QUERY);

  // Api hooks
  const {
    reportPayload,
    pageInfo,
    isLoading,
    error,
    doRequest,
  } =  useGetBoundReportPaginated<BookingCheckInVO[]>({
    requestUrl: reportEndpoints.search,
    requestParams: reportQuery
  });

  // error hooks
  useSnackbarDisplayOnError({error, snackbarShowMessage });

  // conditional display hooks
  // Not gated on `isLoading`: the previous payload survives a refetch, so
  // leaving this on keeps the empty state put instead of blinking out and
  // back on every filter change. It is undefined until the first load, so it
  // cannot flash before there is an answer.
  const shouldShowPersonAndBox = useMemo(() => {
    return !error && reportPayload?.length === 0
  }, [error, reportPayload]);

  const showPagination = useMemo(() => {
    return !!(reportPayload?.length && reportPayload.length > 0);
  }, [reportPayload])

  // change handlers
  const handleFilterChange = (newQuery: SupplierBookingCheckInSearchParams) => {
    const updatedQuery = {
      ...reportQuery,
      ...newQuery
    };
    if (!newQuery.page) {
      // reset to first page on filter change if it is not being explicitly set
      updatedQuery.page = 0;
    }

    setReportQuery(updatedQuery);
  }

  const handleDialogClose = (
    closeProps?: ReportDialogActionCloseProps<BookingCheckInVO>
  ) => {
    switch (closeProps?.action) {
      case 'SUCCESS':
        doRequest?.();
        setReportDialog(getDefaultReportDialog());
        break;
      default:
        setReportDialog(getDefaultReportDialog());
    }
  }

  return (
    <SecurSpaceContainer>
      <SecurSpaceReportContainer>
        <SecurSpaceReportTitle>Equipment Check In</SecurSpaceReportTitle>
        {mobileAppBanner && <MobileAppPromo />}
        <SupplierBookingCheckInSortFilter
          query={reportQuery}
          onChange={handleFilterChange}
        />
        <ReportDataSummary
          visible={!shouldShowPersonAndBox}
          count={pageInfo?.count || 0}
          title={"All"}
          isShowCollapseButton={false}
        />
        <SupplierBookingCheckInCardListWithLoader
          visible={!error}
          isLoading={isLoading}
          items={reportPayload}
          onOpenDialog={setReportDialog}
        />
        <PersonAndBoxMessage
          visible={shouldShowPersonAndBox }
        />
        <BoundTablePagination<SupplierBookingCheckInSearchParams>
          visible={showPagination}
          pageInfo={pageInfo}
          onChange={handleFilterChange}
        />
      </SecurSpaceReportContainer>
      <SupplierBookingCheckInDialogWrapper
        dialog={reportDialog}
        onClose={handleDialogClose}
      />
    </SecurSpaceContainer>
  );
};

export default SupplierBookingCheckInReport;
